import { useState, useEffect } from "react"
import { useForm, router } from "@inertiajs/react"
import { Sparkles, Check, CheckSquare, Square } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import AudioPlayer from "@/components/conversation/audio-player"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"
import { useFlashcardProps } from "@/hooks/use-flashcard-props"

interface Message {
  id: number
  content: string
  translation?: { text: string }
  audio?: { url: string }
  messageExplanation?: {
    romanization: string
    breakdown: Array<{
      thai: string
      romanization: string
      meaning: string
    }>
  }
}

interface Props {
  message: Message
}

export default function NewFlashcard(props: any) {
  const { message } = useFlashcardProps<Props>(props)
  const [selectedWordIndices, setSelectedWordIndices] = useState<number[]>([])
  const [isWordSectionOpen, setIsWordSectionOpen] = useState(false)

  const [localProcessing, setLocalProcessing] = useState(false)
  const {
    data,
    setData,
    processing: formProcessing,
  } = useForm({
    message_id: message.id,
    cards: [
      {
        front_text: message.content,
        back_text: message.translation?.text || "",
        back_romanization: message.messageExplanation?.romanization || "",
        message_id: message.id,
      },
    ],
  })

  const processing = formProcessing || localProcessing

  // Total number of words available
  const totalWords = message.messageExplanation?.breakdown?.length || 0

  // Derived state: are all words selected?
  const allSelected =
    totalWords > 0 && selectedWordIndices.length === totalWords

  const handleWordToggle = (index: number) => {
    setSelectedWordIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    )
  }

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      setSelectedWordIndices([])
    } else {
      // Select all
      setSelectedWordIndices(Array.from({ length: totalWords }, (_, i) => i))
    }
  }

  const handleCreate = () => {
    setLocalProcessing(true)

    // Build cards array with full phrase + selected words
    const cards = [data.cards[0]] // Full phrase card

    // Add word-by-word cards if any selected
    if (
      selectedWordIndices.length > 0 &&
      message.messageExplanation?.breakdown
    ) {
      selectedWordIndices.forEach((index) => {
        const word = message.messageExplanation!.breakdown[index]
        cards.push({
          front_text: word.meaning,
          back_text: word.thai,
          back_romanization: word.romanization,
          message_id: message.id,
        })
      })
    }

    router.post(
      "/flashcards",
      {
        cards,
        message_id: message.id,
      },
      {
        onFinish: () => setLocalProcessing(false),
        // Controller redirects, so onSuccess might not be strictly needed if we rely on server redirect,
        // but explicitly visiting ensures clean navigation if redirect behaves oddly with Inertia.
        // However, standard Inertia handling of 302 is automatic. The previous code had onSuccess router.visit.
        // We'll trust the controller redirect but keep onSuccess just in case.
        onSuccess: () => {
          // Optional: manual visit if needed, but normally server redirect is enough.
        },
      },
    )
  }

  const totalCards = 1 + selectedWordIndices.length

  return (
    <AppLayout
      breadcrumbs={[
        { title: "Flashcards", href: "/flashcards" },
        { title: "New", href: "/flashcards/new" },
      ]}
    >
      <div className="mx-auto max-w-2xl space-y-4 p-4">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Create Flashcard
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Save this message as a flashcard for spaced repetition practice
          </p>
        </div>

        {/* Full phrase card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="size-5 text-yellow-500" />
              Full Phrase Card
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Front (English)
              </label>
              <Textarea
                value={data.cards[0].front_text}
                onChange={(e) =>
                  setData("cards", [
                    {
                      ...data.cards[0],
                      front_text: e.target.value,
                    },
                  ])
                }
                className="min-h-[80px] resize-none"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Back (Thai)
              </label>
              <div className="space-y-3">
                <Textarea
                  value={data.cards[0].back_text}
                  onChange={(e) =>
                    setData("cards", [
                      {
                        ...data.cards[0],
                        back_text: e.target.value,
                      },
                    ])
                  }
                  className="min-h-[80px] resize-none"
                />

                {data.cards[0].back_romanization && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                      Romanization
                    </label>
                    <p className="text-sm text-slate-600 italic dark:text-slate-400">
                      {data.cards[0].back_romanization}
                    </p>
                  </div>
                )}

                {message.audio?.url && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                      Audio
                    </label>
                    <AudioPlayer audioUrl={message.audio.url} variant="dark" />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Word-by-word cards (optional) */}
        {message.messageExplanation?.breakdown &&
          message.messageExplanation.breakdown.length > 0 && (
            <Collapsible
              open={isWordSectionOpen}
              onOpenChange={setIsWordSectionOpen}
            >
              <Card>
                <CardHeader>
                  <CollapsibleTrigger asChild>
                    <button className="flex w-full items-center justify-between text-left hover:opacity-80">
                      <CardTitle className="text-base">
                        Word-by-Word Cards (Optional)
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        {selectedWordIndices.length > 0 && (
                          <span className="text-xs text-slate-600 dark:text-slate-400">
                            {selectedWordIndices.length} selected
                          </span>
                        )}
                        <svg
                          className={cn(
                            "size-4 transition-transform",
                            isWordSectionOpen && "rotate-180",
                          )}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>
                  </CollapsibleTrigger>
                </CardHeader>

                <CollapsibleContent>
                  <CardContent className="space-y-1">
                    {/* Select All Header */}
                    <div className="mb-3 flex items-center justify-between rounded-t-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/50">
                      <label className="flex cursor-pointer items-center gap-3">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={handleSelectAll}
                          className="transition-all duration-150"
                        />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Select All
                        </span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-600 transition-all duration-150 dark:text-slate-400">
                          {selectedWordIndices.length} of {totalWords} selected
                        </span>
                      </div>
                    </div>

                    {/* Individual Word Cards */}
                    <div className="space-y-2">
                      {message.messageExplanation.breakdown.map(
                        (word, index) => {
                          const isSelected = selectedWordIndices.includes(index)
                          return (
                            <label
                              key={index}
                              className={cn(
                                "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-all duration-150 ease-out",
                                isSelected
                                  ? "border-blue-300 bg-blue-50 shadow-sm dark:border-blue-700 dark:bg-blue-950/30"
                                  : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:bg-slate-800",
                                "hover:scale-[1.01] active:scale-[0.99]",
                              )}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleWordToggle(index)}
                                className="mt-0.5 transition-all duration-150"
                              />
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={cn(
                                      "font-medium transition-colors duration-150",
                                      isSelected
                                        ? "text-blue-900 dark:text-blue-100"
                                        : "text-slate-900 dark:text-white",
                                    )}
                                  >
                                    {word.thai}
                                  </span>
                                  <span
                                    className={cn(
                                      "text-sm transition-colors duration-150",
                                      isSelected
                                        ? "text-blue-700 dark:text-blue-300"
                                        : "text-slate-600 dark:text-slate-400",
                                    )}
                                  >
                                    ({word.romanization})
                                  </span>
                                </div>
                                <p
                                  className={cn(
                                    "text-sm transition-colors duration-150",
                                    isSelected
                                      ? "text-blue-800 dark:text-blue-200"
                                      : "text-slate-600 dark:text-slate-400",
                                  )}
                                >
                                  {word.meaning}
                                </p>
                              </div>
                            </label>
                          )
                        },
                      )}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={handleCreate}
            disabled={processing}
            className="flex-1"
          >
            {processing ? (
              "Creating..."
            ) : (
              <>
                <Check className="mr-2 size-4" />
                Create{" "}
                {totalCards > 1 ? `${totalCards} Flashcards` : "Flashcard"}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => window.history.back()}
            disabled={processing}
          >
            Cancel
          </Button>
        </div>
      </div>
    </AppLayout>
  )
}
